'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { CALYPSO } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import H2 from 'UIComponents/elements/headings/H2';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import UILink from 'UIComponents/link/UILink';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIModal from 'UIComponents/dialog/UIModal';
import UISelectableBox from 'UIComponents/button/UISelectableBox';
import Small from 'UIComponents/elements/Small';
import { LEARN_MORE_URLS } from 'ads-account-connection/lib/AdAccountHelpUrls';
import CreateAdAccountSetup from 'AdsUI/components/onboarding/CreateAdAccountSetup';
import { Networks } from 'AdsUI/lib/Constants';
import NetworkIcon from 'AdsUI/components/NetworkIcon';
import { usageTracker } from 'AdsUI/lib/usageTracker';
const GOOGLE_COUPON_LEARN_MORE = 'https://www.hubspot.com/products/marketing/google-ads#Promocode';
const LINKEDIN_COUPON_LEARN_MORE = 'https://www.hubspot.com/linkedin-ads#TermsCondition';

const LearnMoreLink = ({
  children,
  href
}) => /*#__PURE__*/_jsx(UILink, {
  external: true,
  href: href,
  onClick: e => e.stopPropagation(),
  children: children
});

const NetworkButtonWrapper = ({
  align = 'center',
  children,
  network,
  onClick
}) => {
  const handleClick = useCallback(() => onClick(network), [network, onClick]);
  return /*#__PURE__*/_jsx(UISelectableBox, {
    block: true,
    className: "m-top-2 p-y-2 p-x-5",
    onClick: handleClick,
    selected: false,
    children: /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      justify: "between",
      className: "m-y-2",
      children: [/*#__PURE__*/_jsx(UIMediaObject, {
        itemLeft: /*#__PURE__*/_jsx(NetworkIcon, {
          network: network
        }),
        align: align,
        children: /*#__PURE__*/_jsx("div", {
          className: "text-left",
          children: children
        })
      }), network !== Networks.ADWORDS && /*#__PURE__*/_jsx(UIIcon, {
        color: CALYPSO,
        size: 12,
        name: "externalLink"
      })]
    })
  });
};

const CreateAdAccountModal = ({
  onClose,
  isDeveloperPortal
}) => {
  const [adwordsSelected, setAdwordsSelected] = useState(false);
  const handleClick = useCallback(network => {
    if (network === Networks.ADWORDS) {
      setAdwordsSelected(true);
    } else {
      window.open(LEARN_MORE_URLS[network], '_blank');
    }

    usageTracker.track('adAccountConnectAction', {
      action: 'Clicked create ad account',
      network
    });
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIModal, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: onClose
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.newAdAccount"
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "coupon.createAdAccount"
          })
        }), /*#__PURE__*/_jsxs(UIList, {
          className: "m-bottom-4",
          children: [/*#__PURE__*/_jsx(NetworkButtonWrapper, {
            network: Networks.FACEBOOK,
            onClick: handleClick,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: Networks.FACEBOOK.toLowerCase()
            })
          }), /*#__PURE__*/_jsxs(NetworkButtonWrapper, {
            network: Networks.ADWORDS,
            align: "start",
            onClick: handleClick,
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: Networks.ADWORDS.toLowerCase()
            }), !isDeveloperPortal && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Small, {
                use: "help",
                style: {
                  whiteSpace: 'normal'
                },
                children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                  message: "coupon.googleSpecial_jsx",
                  options: {
                    href: GOOGLE_COUPON_LEARN_MORE
                  },
                  elements: {
                    Link: LearnMoreLink
                  }
                })
              })]
            })]
          }), /*#__PURE__*/_jsxs(NetworkButtonWrapper, {
            network: Networks.LINKEDIN,
            onClick: handleClick,
            align: "start",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: Networks.LINKEDIN.toLowerCase()
            }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Small, {
              use: "help",
              style: {
                whiteSpace: 'normal'
              },
              children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "coupon.linkedinSpecial_jsx",
                options: {
                  href: LINKEDIN_COUPON_LEARN_MORE
                },
                elements: {
                  Link: LearnMoreLink
                }
              })
            })]
          })]
        })]
      })]
    }), adwordsSelected && /*#__PURE__*/_jsx(CreateAdAccountSetup, {
      onClose: () => setAdwordsSelected(false),
      networkSelected: Networks.ADWORDS,
      isDeveloperPortal: isDeveloperPortal
    })]
  });
};

const CreateAdAccount = ({
  isDeveloperPortal
}) => {
  const [showModal, setShowModal] = useState(false);
  const handleClick = useCallback(() => {
    setShowModal(true);
    usageTracker.track('adAccountConnectAction', {
      action: 'Opened create ad account modal'
    });
  }, [setShowModal]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UILink, {
      onClick: handleClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.newAdAccount"
      })
    }), showModal && /*#__PURE__*/_jsx(CreateAdAccountModal, {
      onClose: () => setShowModal(false),
      isDeveloperPortal: isDeveloperPortal
    })]
  });
};

export default CreateAdAccount;